import { Component, ContentChild, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { CarouselBreakpoint, InfiniteCarousel } from '../../utils/infinite-carousel';

/**
 * Carrusel nativo compartido (reemplaza ngx-owl-carousel-o). El contenido de cada slide lo define
 * quien lo usa con <ng-template #slideTemplate let-item let-i="index">, así cada carrusel conserva
 * sus propios botones/iconos sin que este componente sepa nada de eso. Ver README de uso en
 * shortcuts.component.html para un ejemplo con autoplay + flechas reposicionadas.
 */
@Component({
  selector: 'usb-carousel',
  templateUrl: './usb-carousel.component.html',
  styleUrls: ['./usb-carousel.component.scss']
})
export class UsbCarouselComponent<T = any> implements OnInit, OnChanges, OnDestroy {

  @Input() items: T[] = [];

  // Dos formas de definir cuántos ítems se ven por ancho: un par mobile/desktop simple, o una
  // lista de tramos tipo "responsive" de owl (ver InfiniteCarousel). Si se pasa breakpoints, los
  // otros tres se ignoran.
  @Input() mobileBreakpoint = 400;
  @Input() mobileItemsPerView = 1;
  @Input() desktopItemsPerView = 2;
  @Input() breakpoints?: CarouselBreakpoint[];

  @Input() autoplay = false;
  @Input() autoplayInterval = 3000; // ms, igual semántica que autoplayTimeout de owl
  @Input() pauseOnHover = true; // igual que autoplayHoverPause de owl
  @Input() showArrows = true;
  @Input() trackByFn?: (index: number, item: T) => any;

  @ContentChild('slideTemplate') slideTemplate!: TemplateRef<{ $implicit: T; index: number }>;

  // Opcionales: si algún carrusel necesita una flecha totalmente distinta (no solo reposicionada/
  // reestilada vía las CSS custom properties del host), puede reemplazar el botón por defecto.
  @ContentChild('prevArrow') prevArrowTemplate?: TemplateRef<{ prev: () => void }>;
  @ContentChild('nextArrow') nextArrowTemplate?: TemplateRef<{ next: () => void }>;

  carousel = new InfiniteCarousel<T>();

  private autoplayHandle: any;

  /**
   * realIndex (índice real dentro de `items`, sin contar los clones de
   * borde) de las slides actualmente visibles en el viewport, en orden
   * visual izquierda->derecha. Útil para quien consume el carrusel y
   * necesita saber "qué posición ocupa este item DENTRO de lo que se ve
   * ahora" (ej. un degradado de color que siempre debe ir de punta a
   * punta en la página visible, sin importar cuántos items haya en total
   * ni en qué parte del scroll infinito estemos).
   */
  get visibleRealIndices(): number[] {
    const perView = this.carousel.itemsPerView;
    const start = this.carousel.currentIndex;
    return this.carousel.extended.slice(start, start + perView).map(slide => slide.realIndex);
  }

  ngOnInit(): void {
    this.carousel = new InfiniteCarousel<T>(this.mobileBreakpoint, this.mobileItemsPerView, this.desktopItemsPerView, this.breakpoints);
    this.carousel.setViewportWidth(window.innerWidth);
    this.carousel.setItems(this.items);
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.carousel && changes.items && !changes.items.firstChange) {
      this.carousel.setItems(this.items);
    }
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.carousel.setViewportWidth(window.innerWidth);
  }

  onMouseEnter(): void {
    if (this.autoplay && this.pauseOnHover) {
      this.stopAutoplay();
    }
  }

  onMouseLeave(): void {
    if (this.autoplay && this.pauseOnHover) {
      this.startAutoplay();
    }
  }

  trackBySlide = (_index: number, slide: { realIndex: number }): any => {
    return this.trackByFn ? this.trackByFn(slide.realIndex, this.items[slide.realIndex]) : slide.realIndex;
  };

  startAutoplay(): void {
    this.stopAutoplay();
    this.autoplayHandle = setInterval(() => this.carousel.next(), this.autoplayInterval);
  }

  stopAutoplay(): void {
    clearInterval(this.autoplayHandle);
  }

}
